import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './contexts/LanguageContext';
import { PlayerProvider } from './contexts/PlayerContext';
import FluidBackgroundV2 from './components/FluidBackgroundV2';
import IntroScreen from './components/IntroScreen';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PersistentPlayer from './components/PersistentPlayer';
import PageTransition from './components/PageTransition';
import HomePageV2 from './pages/HomePageV2';
import VisionPage from './pages/VisionPage';
import EventsPage from './pages/EventsPage';
import ArtistsPage from './pages/ArtistsPage';
import ArtistFromNow from './pages/ArtistFromNow';
import ArtistRVRE from './pages/ArtistRVRE';
import EventMetroClub from './pages/EventMetroClub';
import EventWithin from './pages/EventWithin';
import WithinGalleryPage from './pages/WithinGalleryPage';
import ContactPage from './pages/ContactPage';
import FairflowPage from './pages/FairflowPage';
import ReleasesPage from './pages/ReleasesPage';
import PartnersPage from './pages/PartnersPage';
import NotFoundPage from './pages/NotFoundPage';
import LegalNoticePage from './pages/legal/LegalNoticePage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import RefundPolicyPage from './pages/legal/RefundPolicyPage';
import EventTermsPage from './pages/legal/EventTermsPage';
import './App.css';

// Inner app component that uses router hooks
function AppContent() {
  const location = useLocation();

  return (
    <>
      <Navbar />
      <PageTransition>
        <main>
          <Routes location={location}>
            <Route path="/" element={<HomePageV2 />} />
            <Route path="/vision" element={<VisionPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/metro-club" element={<EventMetroClub />} />
            <Route path="/events/within" element={<EventWithin />} />
            <Route path="/events/within/gallery" element={<WithinGalleryPage />} />
            <Route path="/artists" element={<ArtistsPage />} />
            <Route path="/artists/fromnow" element={<ArtistFromNow />} />
            <Route path="/artists/rvre" element={<ArtistRVRE />} />
            <Route path="/releases" element={<ReleasesPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/fairflow" element={<FairflowPage />} />
            <Route path="/legal/notice" element={<LegalNoticePage />} />
            <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/legal/terms" element={<TermsOfServicePage />} />
            <Route path="/legal/refund" element={<RefundPolicyPage />} />
            <Route path="/legal/events" element={<EventTermsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
      </PageTransition>
      <Footer />
    </>
  );
}

function App() {
  const [showIntro, setShowIntro] = useState(false);
  const [introComplete, setIntroComplete] = useState(true);

  useEffect(() => {
    // Check if first visit
    const hasEntered = localStorage.getItem('vvs_entered');
    if (!hasEntered) {
      setShowIntro(true);
      setIntroComplete(false);
    }
  }, []);

  const handleIntroComplete = () => {
    setShowIntro(false);
    setIntroComplete(true);
  };

  return (
    <HelmetProvider>
      <LanguageProvider>
        <PlayerProvider>
          {/* Intro screen - first visit only */}
          {showIntro && <IntroScreen onEnter={handleIntroComplete} />}
          
          {/* Main app - always rendered but hidden during intro */}
          <div style={{ 
            opacity: introComplete ? 1 : 0,
            pointerEvents: introComplete ? 'auto' : 'none',
          }}>
            <BrowserRouter>
              <FluidBackgroundV2 />
              <AppContent />
              {/* Persistent player - outside of router, never unmounts */}
              <PersistentPlayer />
            </BrowserRouter>
          </div>
        </PlayerProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
